<template>
  <!--
    `close-on-content-click` stays off, the partner and fleet selectors are autocompletes the user
    has to be able to click into. The entries that navigate close the menu themselves instead.
  -->
  <v-menu
    v-model="isMenuOpen"
    offset-y
    left
    nudge-bottom="14"
    max-width="300"
    :close-on-content-click="false"
    content-class="user-profile-menu-content"
  >
    <template v-slot:activator="{ on, attrs }">
      <span>
        <user-avatar
          v-if="user.firstName && user.lastName"
          data-test-user-menu
          :user="user"
          size="35px"
          :color="color"
          class="ml-4"
          v-bind="attrs"
          v-on="on"
        />
      </span>
    </template>
    <v-list>
      <v-list-item>
        <v-list-item-avatar>
          <user-avatar v-if="user.firstName && user.lastName" :user="user" :color="color" size="35px" />
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title> {{ user.firstName }} {{ user.lastName }}</v-list-item-title>
          <v-list-item-subtitle class="text--disabled">{{ user.userName }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="false">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                :to="{ name: 'Profile' }"
                aria-label="Anpassen"
                data-test-profile-edit
                v-bind="attrs"
                v-on="on"
                @click="closeMenu"
              >
                <v-icon size="18">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span data-test-profile-edit-tooltip>Anpassen</span>
          </v-tooltip>
        </v-list-item-action>
      </v-list-item>
      <v-divider
        class="my-2 pb-1"
        v-if="partners.length > 0 && $can(ActionEnum.READ, ResourceEnum.PARTNER)"
      ></v-divider>

      <v-list-item v-if="partners.length > 0">
        <the-partner-selector-list
          v-if="$can(ActionEnum.READ, ResourceEnum.PARTNER) && partners.length > 1"
          @change="closeMenu"
        ></the-partner-selector-list>
        <v-list-item-content v-else>
          <v-list-item-title>{{ partners[0].companyName }}</v-list-item-title>
          <v-list-item-subtitle class="text--disabled">{{ partners[0].id }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-list-item v-if="isReport && permissionFleetIds.length">
        <v-list-item-content v-if="permissionFleets.length === 1">
          <v-list-item-title>{{ firstParentFleet.title }}</v-list-item-title>
          <v-list-item-subtitle class="text--disabled">
            {{ firstParentFleet.id }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <the-parent-fleet-selector-list v-else @fleetChanged="closeMenu" />
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <template v-if="isUserMenuNavigation">
        <v-list-item
          v-for="item in profileMenu"
          :key="item.name"
          link
          :to="to(item)"
          data-test-profile-menu-link
          @click="closeMenu"
          dense
        >
          <v-list-item-icon class="me-2">
            <v-icon size="22">
              {{ icon(item) }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-else>
        <v-list-item link :to="userItem.link" data-test-profile-link @click="closeMenu">
          <v-list-item-icon class="me-2">
            <v-icon size="22">
              {{ userItem.icon }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ userItem.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <v-divider class="my-2"></v-divider>

      <!-- Logout -->
      <v-list-item @click="performLogout">
        <v-list-item-icon class="me-2" v-if="!isLogoutLoading">
          <v-icon size="22">
            {{ logOut.icon }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-icon class="me-2" v-else>
          <v-progress-circular :size="22" :width="3" color="grey" indeterminate></v-progress-circular>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ logOut.text }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import UserAvatar from "@/components/utility/UserAvatar.vue";
import TheParentFleetSelectorList from "@/layouts/navigation/TheParentFleetSelector.vue";
import ThePartnerSelectorList from "@/layouts/navigation/ThePartnerSelectorList.vue";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { resolveNavigationIcon } from "@/lib/utility/navigation-icon";
import { IUser } from "@/models/user.entity";
import { MrfiktivFleetViewModelGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { NavigationRoute } from "@/store/mapper/navigation.route";
import { ConfigModule } from "@/store/modules/config";
import { FleetManagerModule } from "@/store/modules/fleet/manager-fleet.store";
import { UserModule } from "@/store/modules/me-user.store";
import { NavigationModule } from "@/store/modules/navigation.store";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { RawLocation } from "vue-router";

@Component({
  components: { ThePartnerSelectorList, TheParentFleetSelectorList, UserAvatar }
})
export default class ProfileMenu extends Vue {
  @Prop({ default: false })
  isEndUser!: boolean;

  ActionEnum = ActionEnum;
  ResourceEnum = ResourceEnum;

  isMenuOpen = false;

  /**
   * The menu lives in `TheAppShell`, which outlives the view: a navigation no longer tears it down,
   * so it has to close itself. Closes every entry that navigates, and the watcher catches the ones
   * that start a navigation somewhere else.
   */
  @Watch("$route")
  closeMenu() {
    this.isMenuOpen = false;
  }

  get partner() {
    return PartnerModule.partner;
  }

  get partners() {
    return PartnerModule.partners;
  }

  get companyName() {
    return PartnerModule.partner?.companyName || "";
  }

  get isReport() {
    return AppContextEnum.REPORT === ConfigModule.appContext;
  }

  get fleetPartnerPermission() {
    if (this.partner.id) {
      return UserModule.fleetPermission.filter((p) => this.partner.id === p.partnerId);
    }

    return UserModule.fleetPermission;
  }

  /**
   * Distinct list of fleetId found in fleet permission for current partner.
   */
  get permissionFleetIds() {
    return [...new Set(this.fleetPartnerPermission.map((p) => p.fleetId))];
  }

  get permissionFleets(): MrfiktivFleetViewModelGen[] {
    return FleetManagerModule.permissionFleets.filter((f) => f.partnerId === this.partner.id);
  }

  get parentFleets(): MrfiktivFleetViewModelGen[] {
    return FleetManagerModule.fleets;
  }

  get firstParentFleet() {
    return this.parentFleets?.[0];
  }

  /**
   * Returns primary color if !enduser, else returns custom partner color
   */
  get color() {
    if (this.isEndUser) {
      return getDefaultPartnerColor();
    } else {
      return "primary";
    }
  }

  get isAuthenticated() {
    return UserModule.isAuthenticated;
  }

  get user(): IUser {
    return UserModule.user;
  }

  get userItem() {
    const item = {
      icon: "mdi-account-outline",
      text: "Profil",
      link: "/profile"
    };
    return item;
  }

  get profileMenu(): NavigationRoute[] {
    return NavigationModule.profileMenu;
  }

  get isUserMenuNavigation(): boolean {
    return NavigationModule.navigationConfiguration.isUserMenuNavigation;
  }

  icon(item: NavigationRoute): string | undefined {
    return resolveNavigationIcon(item.navigation?.icon);
  }

  to(item: NavigationRoute): RawLocation {
    return { name: item.name, params: { partnerId: this.partner.id } };
  }

  get logOut() {
    const item = {
      icon: "mdi-logout",
      text: "Logout",
      link: "/login/logout"
    };
    return item;
  }

  isLogoutLoading = false;

  async performLogout() {
    this.closeMenu();
    this.isLogoutLoading = true;
    try {
      await UserModule.logout();
    } catch {
      this.isLogoutLoading = false;
    }
  }
}
</script>

<style lang="scss">
.user-profile-menu-content {
  .v-list-item {
    min-height: 2.5rem !important;
  }
}
</style>
